import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import i18n from 'i18n';
import { ReactComponent as CommissionTransactionIcon } from 'assets/iconButtons/commissions.svg';
import { ReactComponent as PaymentTransactionIcon } from 'assets/iconButtons/transactions.svg';
import { ReactComponent as TransitTransactionIcon } from 'assets/iconButtons/transit-transaction.svg';
import { ReactComponent as CryptoTransactionIcon } from 'assets/iconButtons/crypto.svg';
import { ReactComponent as ExchangeTransactionIcon } from 'assets/iconButtons/currency-exchange.svg';
import { ReactComponent as RepeatTransactionIcon } from 'assets/iconButtons/repeat.svg';
import { CUSTOM_TABS_DIRECTION } from 'components/constants';
import CustomTabs from 'components/CustomTabs';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import { hasRole, TRANSACTION_CREATE } from 'services/roles';
import { Container } from 'uikit/Container/Container';
import './CreateTransaction.scss';

// A tab with a role is shown to the operators with this role only
const CREATE_TRANSACTION_TABS = [
  {
    name: 'payment',
    title: i18n.getMessage('createTransaction.tab.payment'),
    icon: PaymentTransactionIcon,
    route: ROUTE_PATHS.CREATE_PAYMENT_TRANSACTION
  },
  {
    name: 'transit',
    title: i18n.getMessage('createTransaction.tab.transit'),
    icon: TransitTransactionIcon,
    route: ROUTE_PATHS.CREATE_TRANSIT_TRANSACTION
  },
  {
    name: 'commission',
    title: i18n.getMessage('createTransaction.tab.commission'),
    icon: CommissionTransactionIcon,
    route: ROUTE_PATHS.CREATE_COMMISSION_TRANSACTION
  },
  {
    name: 'crypto',
    title: i18n.getMessage('createTransaction.tab.crypto'),
    icon: CryptoTransactionIcon,
    route: ROUTE_PATHS.CREATE_CRYPTO_TRANSACTION
  },
  {
    name: 'exchange',
    title: i18n.getMessage('createTransaction.tab.exchange'),
    icon: ExchangeTransactionIcon,
    route: ROUTE_PATHS.CREATE_EXCHANGE_TRANSACTION,
    role: TRANSACTION_CREATE
  },
  {
    name: 'repeat',
    title: i18n.getMessage('createTransaction.tab.repeat'),
    icon: RepeatTransactionIcon,
    route: ROUTE_PATHS.CREATE_REPEAT_TRANSACTION
  }
];

const CreateTransaction = () => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(null);
  // The roles are checked when the page is opened - the module is loaded before the login, when they are not known yet
  const tabList = useMemo(() => CREATE_TRANSACTION_TABS.filter((tab) => !tab.role || hasRole(tab.role)), []);
  const [isCompactTabs, setIsCompactTabs] = useState(false);
  const tabsRef = useRef(null);
  const fullTabsRef = useRef(null);

  // Only the icons of the tabs are shown when the tabs with the titles do not fit in one line. The width of the tabs
  // with the titles is taken from their hidden copy, as the shown ones may be without the titles.
  // The elements are taken once - the refs are cleared on unmount before the observer is disconnected.
  useEffect(() => {
    const tabs = tabsRef.current;
    const fullTabs = fullTabsRef.current;
    const observer = new ResizeObserver(() => {
      setIsCompactTabs(tabs.clientWidth < fullTabs.offsetWidth);
    });

    observer.observe(tabs);
    observer.observe(fullTabs);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const path = location.pathname;

    const activeTabFromPath = tabList.find((tab) => tab.route === path);
    if (activeTabFromPath) {
      setActiveTab(activeTabFromPath.name);
    } else if (path === ROUTE_PATHS.CREATE_TRANSACTION) {
      setActiveTab(tabList[0].name);
      navigate(tabList[0].route);
    }
  }, [location.pathname, navigate, tabList]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    const selectedTab = tabList.find((tab) => tab.name === tabName);
    if (selectedTab) {
      navigate(selectedTab.route);
    }
  };

  return (
    <section className={'right-section'}>
      <Container className={'create-transaction-container'} header={i18n.getMessage('container.createTransaction')}>
        <div className={'create-transaction-wrapper'}>
          <div
            ref={tabsRef}
            className={`create-transaction-tabs ${isCompactTabs ? 'create-transaction-tabs-compact' : ''}`}
          >
            <CustomTabs
              tabList={tabList}
              direction={CUSTOM_TABS_DIRECTION.HORIZONTAL}
              activeTab={activeTab}
              setActiveTab={handleTabChange}
            />
            <div className={'create-transaction-tabs-measure'} aria-hidden={'true'}>
              <div ref={fullTabsRef} className={'create-transaction-tabs-measure-content'}>
                <CustomTabs
                  tabList={tabList}
                  direction={CUSTOM_TABS_DIRECTION.HORIZONTAL}
                  activeTab={activeTab}
                  setActiveTab={() => {}}
                />
              </div>
            </div>
          </div>
          <Outlet />
        </div>
      </Container>
    </section>
  );
};

export default CreateTransaction;
